import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { useAgGridCommon } from '../../../common/table/agGrid/AgGridCommon';
import getColumnDefs from './columnDefs';
import FinanceAccount from '../../../ts/models/FinanceAccount.model';
import { common, mergeGridOpts } from 'utils/agGrid/options';
import { GridOptions } from 'ag-grid-community';
import { useAppSelector } from 'hooks/useAppSelector';
import { createLoadingSelector } from 'erisxkit/client';
import { ACCOUNTS } from 'constants/actionTypes';

interface FinanceAccountsGridProps {
  data: FinanceAccount[];
  onSelectAccount: (accountId: string) => void;
}

const gridOptions: GridOptions = mergeGridOpts(common, {
  rowStyle: { cursor: 'pointer' },
});
export const gridName = 'finance-accounts';

const FinanceAccountsGrid: React.FC<FinanceAccountsGridProps> = ({
  data,
  onSelectAccount,
}) => {
  const gridRef = useRef<AgGridReact>(null);

  const columnDefs = useMemo(
    () => getColumnDefs(onSelectAccount),
    [onSelectAccount],
  );
  const loading = useAppSelector((state) =>
    createLoadingSelector([ACCOUNTS])(state),
  );
  const { gridVisible, initialGridSettings, onStateUpdated } = useAgGridCommon({
    gridName,
  });

  useEffect(() => {
    if (!initialGridSettings?.columnSizing) {
      gridRef.current?.api?.sizeColumnsToFit();
    }
  }, [initialGridSettings?.columnSizing]);

  const onRowClicked = useCallback(
    ({ data }: { data: FinanceAccount }) => {
      const accountId = data?.accountId;
      if (accountId) {
        onSelectAccount(accountId);
      }
    },
    [onSelectAccount],
  );

  return (
    <div className="ag-theme-quartz">
      {gridVisible && (
        <AgGridReact
          loading={loading}
          ref={gridRef}
          rowData={data}
          columnDefs={columnDefs}
          gridOptions={gridOptions}
          onStateUpdated={onStateUpdated}
          initialState={initialGridSettings}
          onRowClicked={onRowClicked}
        />
      )}
    </div>
  );
};

export default FinanceAccountsGrid;
